import { useCallback, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { TFunction } from "i18next";
import {
  Users,
  LayoutGrid,
  MapIcon as Map,
  Crosshair,
} from "../../../../components/icons";
import SoccerField from "../../../../components/SoccerField";
import PlayerAvatar from "../../../../components/PlayerAvatar";
import TacticalField from "./TacticalField";
import type { TeamColorInfo } from "./TeamSelector";
import { resolveTeamPalette } from "../../../../lib/teamPalette";
import {
  getPositionGroup,
  type TacticalPosition,
  type Formation,
  type PositionGroup,
} from "../../hooks/useTacticalPositions";
import { FieldCoordinate, Match, Player, StaffRole } from "../../types";
import { formatDisplayName } from "../../../../lib/nameFormat";

const POSITION_GROUP_ORDER: PositionGroup[] = [
  "goalkeeper",
  "defense",
  "midfield",
  "attack",
  "other",
];

/** Reused by `CardSubjectModal` (PMA-COCK-120) for its own on-pitch/bench sections. */
export const getGroupMeta = (group: PositionGroup) => {
  switch (group) {
    case "goalkeeper":
      return {
        label: "GK",
        dot: "bg-cyan-400",
        border: "border-cyan-500/60",
        badge: "bg-cyan-500/10 text-cyan-200 border-cyan-500/40",
      };
    case "defense":
      return {
        label: "DEF",
        dot: "bg-emerald-400",
        border: "border-emerald-500/60",
        badge: "bg-emerald-500/10 text-emerald-200 border-emerald-500/40",
      };
    case "midfield":
      return {
        label: "MID",
        dot: "bg-amber-400",
        border: "border-amber-500/60",
        badge: "bg-amber-500/10 text-amber-200 border-amber-500/40",
      };
    case "attack":
      return {
        label: "ATT",
        dot: "bg-rose-400",
        border: "border-rose-500/60",
        badge: "bg-rose-500/10 text-rose-200 border-rose-500/40",
      };
    default:
      return {
        label: "OTH",
        dot: "bg-slate-400",
        border: "border-slate-500/50",
        badge: "bg-slate-500/10 text-slate-200 border-slate-500/40",
      };
  }
};

/** Reused by `CardSubjectModal` (PMA-COCK-120). */
export const sortPlayersByGroup = (players: Player[]) =>
  [...players].sort((a, b) => {
    const groupA = getPositionGroup(a.position);
    const groupB = getPositionGroup(b.position);
    const groupDelta =
      POSITION_GROUP_ORDER.indexOf(groupA) -
      POSITION_GROUP_ORDER.indexOf(groupB);
    if (groupDelta !== 0) return groupDelta;
    return a.jersey_number - b.jersey_number;
  });

/**
 * "HEAD_COACH" is the only non-free-text role value — MatchStaffRole is
 * retired, a manager-sourced entry is always the literal "HEAD_COACH", and
 * there is no other fixed token (ASSISTANT_1/ASSISTANT_2 no longer exist as
 * role values anywhere in the contract). Any other role is free text — the
 * TechnicalStaffRole value the team CRUD wrote (e.g. "Fitness Coach") —
 * looked up against the SAME translation map the team screen uses
 * (`admin.technicalStaffRoles`, `TeamsManager.tsx`) so a person's role label
 * reads identically in both places. `defaultValue` renders the raw stored
 * string for anything not in that map (an ingested or hand-edited role)
 * instead of blanking the chip. Reused by `CardSubjectModal` (PMA-COCK-120).
 */
export const staffRoleLabel = (
  t: TFunction<"logger">,
  role: StaffRole,
): string => {
  if (role === "HEAD_COACH") return t("staffRoleHeadCoach", "HC");
  return t(`technicalStaffRoles.${role}`, {
    ns: "admin",
    defaultValue: role,
  });
};

interface PlayerSelectorPanelProps {
  match: Match;
  flipSides?: boolean;
  selectedPlayer: Player | null;
  selectedTeam: "home" | "away" | "both";
  expelledPlayerIds?: Set<string>;
  disciplinaryStatusByPlayer?: Record<
    string,
    { yellowCount: number; red: boolean }
  >;
  /**
   * PMA-COCK-145: per-player goal count (own goals excluded — see
   * `useGoalTally.ts`), keyed by player id so it survives substitutions.
   * Drives the goal-ball indicator on the "En cancha" (on-pitch) list row.
   */
  goalCountByPlayerId?: Record<string, number>;
  onFieldIds: { home: Set<string>; away: Set<string> };
  onPlayerClick: (player: Player) => void;
  onFieldPlayerClick?: (
    player: Player,
    anchor: { xPercent: number; yPercent: number },
    location: [number, number],
    side: "home" | "away",
  ) => void;
  onFieldDestinationClick?: (coordinate: FieldCoordinate) => void;
  fieldOverlay?: React.ReactNode;
  /**
   * PMA-COCK-185: true while `fieldOverlay` is the `GoalPlacementOverlay`
   * (an eligible Shot just confirmed) — forwarded to `TacticalField`'s own
   * `overlayBlocksPitch` so token taps/coordinate taps under the scrim are
   * inert. Tactical view only, the same scope the overlay itself renders
   * in via `TacticalField`'s `overlay` slot — `SoccerField`'s "field" mode
   * fallback is untouched (out of this UC's scope, see team_findings.md).
   */
  overlayBlocksPitch?: boolean;
  forceFieldMode?: boolean;
  forceTacticalMode?: boolean;
  priorityPlayerId?: string | null;
  isReadOnly?: boolean;
  showDestinationControls?: boolean;
  /**
   * EPIC-96 (T4): forwarded to TacticalField's `shotGuideEdge` — the
   * non-interactive pitch guide + tap-feedback ripple. Tactical view only;
   * SoccerField (the "field" mode fallback) has no goal-mouth geometry of
   * its own and never receives this prop.
   */
  shotGuideEdge?: "left" | "right";
  /** Tactical field support */
  getDisplayPosition?: (
    playerId: string,
    flipSides: boolean,
  ) => TacticalPosition;
  onPlayerDragEnd?: (
    playerId: string,
    displayX: number,
    displayY: number,
    playerPosition: string | undefined,
    side: "home" | "away",
  ) => void;
  draggingPlayerId?: string | null;
  onTacticalDragStart?: (playerId: string) => void;
  onTacticalDragStop?: () => void;
  allTacticalPositions?: Map<string, TacticalPosition>;
  homeFormation?: Formation | null;
  awayFormation?: Formation | null;
  applyFormation?: (side: "home" | "away", formation: Formation | null) => void;
  /** When set, only these player nodes are visible on the field */
  visiblePlayerIds?: Set<string>;
  /** When true (live match), expand tactical drag bounds to full field. */
  isMatchLive?: boolean;
  /**
   * When true (default), players can be dragged/repositioned on the field
   * even when `isReadOnly` is true (e.g. pre-match / paused).
   * Set to false only if drag must also be disabled.
   */
  repositionEnabled?: boolean;
  /**
   * When true (default), the tactical view's drag-only hint mentions that
   * substitutions remain available via the rail's Quick Substitution panel
   * even while `isReadOnly` is true — real substitutions happen during
   * clock stoppages (throw-in, goal kick). Set to false when they are
   * genuinely blocked (e.g. VAR review, match not live).
   */
  substitutionEnabled?: boolean;
  headerAction?: ReactNode;
  /**
   * EPIC-94 (AC1, lead review round 1, 2026-09-23): overrides the panel's
   * own "Select Player"/"Select a Player" title for a step where a player
   * is already selected and no further selection is being asked for (e.g.
   * the Goal quick action's pitch-tap step) — that title otherwise
   * contradicts the field hint shown below it. Only ever set from
   * `ActionStage.tsx`, this component's sole real caller — undefined
   * (default) keeps the existing title unchanged for every other step.
   */
  headerLabelOverride?: string;
  /** Color picker — pass team color info to enable chip swatches on the tactical field. */
  homeTeamColorInfo?: TeamColorInfo;
  awayTeamColorInfo?: TeamColorInfo;
  /** Called after a successful color patch so parent can refresh team state. */
  onTeamColorSaved?: (
    teamId: string,
    primary: string | null,
    secondary: string | null,
  ) => void;
  t: TFunction<"logger">;
}

const PlayerSelectorPanel = ({
  match,
  flipSides = false,
  selectedPlayer,
  selectedTeam,
  expelledPlayerIds,
  disciplinaryStatusByPlayer,
  goalCountByPlayerId,
  onFieldIds,
  onPlayerClick,
  onFieldPlayerClick,
  onFieldDestinationClick,
  fieldOverlay,
  overlayBlocksPitch = false,
  forceFieldMode = false,
  forceTacticalMode = false,
  priorityPlayerId = null,
  isReadOnly = false,
  showDestinationControls = false,
  shotGuideEdge,
  getDisplayPosition,
  onPlayerDragEnd,
  draggingPlayerId,
  onTacticalDragStart,
  onTacticalDragStop,
  allTacticalPositions,
  homeFormation: _homeFormation,
  awayFormation: _awayFormation,
  applyFormation: _applyFormation,
  visiblePlayerIds,
  isMatchLive = false,
  repositionEnabled = true,
  substitutionEnabled = true,
  headerAction,
  headerLabelOverride,
  homeTeamColorInfo,
  awayTeamColorInfo,
  onTeamColorSaved,
  t,
}: PlayerSelectorPanelProps) => {
  const [viewMode, setViewMode] = useState<"list" | "field" | "tactical">(
    forceTacticalMode ? "tactical" : forceFieldMode ? "field" : "list",
  );
  const resolvedViewMode = forceTacticalMode
    ? "tactical"
    : forceFieldMode
      ? "field"
      : viewMode;

  const homePalette = resolveTeamPalette(match.home_team);
  const awayPalette = resolveTeamPalette(match.away_team);

  useEffect(() => {
    if (forceTacticalMode) {
      setViewMode("tactical");
      return;
    }
    if (!forceFieldMode) return;
    setViewMode("field");
  }, [forceFieldMode, forceTacticalMode]);
  const prioritizePlayers = (players: Player[]) => {
    if (!priorityPlayerId) return players;
    const index = players.findIndex((p) => p.id === priorityPlayerId);
    if (index <= 0) return players;
    const prioritized = [
      players[index],
      ...players.slice(0, index),
      ...players.slice(index + 1),
    ];
    return prioritized;
  };

  // Filter teams based on selected team
  const teamsToShow =
    selectedTeam === "home"
      ? [
          {
            labelColor: "bg-red-600" as const,
            team: match.home_team,
            tone: "home" as const,
          },
        ]
      : selectedTeam === "away"
        ? [
            {
              labelColor: "bg-blue-600" as const,
              team: match.away_team,
              tone: "away" as const,
            },
          ]
        : [
            {
              labelColor: "bg-red-600" as const,
              team: match.home_team,
              tone: "home" as const,
            },
            {
              labelColor: "bg-blue-600" as const,
              team: match.away_team,
              tone: "away" as const,
            },
          ];

  const renderPlayerGrid = (
    players: Player[],
    tone: "home" | "away",
    disabled = false,
  ) => (
    <div className="grid gap-2 grid-cols-1">
      {sortPlayersByGroup(players).map((player) => {
        const group = getPositionGroup(player.position);
        const meta = getGroupMeta(group);
        const isSelected = selectedPlayer?.id === player.id;
        const isExpelled = expelledPlayerIds?.has(player.id) ?? false;
        const isDisabled = disabled || isExpelled;
        return (
          <button
            key={player.id}
            data-testid={`player-card-${player.id}`}
            data-player-row="true"
            data-position-group={group}
            onClick={() => !isDisabled && onPlayerClick(player)}
            disabled={isDisabled}
            className={`w-full max-w-[420px] mx-auto px-3 py-2 rounded-lg border-l-4 text-left transition-all ${
              isDisabled
                ? "bg-slate-900 border-slate-800 opacity-50 cursor-not-allowed"
                : isSelected
                  ? "bg-blue-900/40 border-blue-500 ring-1 ring-blue-500"
                  : tone === "home"
                    ? "bg-red-900/20 border-red-900/50 hover:border-red-500/50 hover:bg-red-900/30"
                    : "bg-blue-900/20 border-blue-900/50 hover:border-blue-500/50 hover:bg-blue-900/30"
            } ${meta.border}`}
          >
            <div className="flex items-center gap-2.5">
              <PlayerAvatar player={player} size={20} />
              <span
                data-testid={`player-number-${player.id}`}
                className={`text-xs font-bold min-w-[2.25rem] ${
                  isDisabled ? "text-slate-600" : "text-slate-200"
                }`}
              >
                #{player.jersey_number}
              </span>
              <span
                data-testid={`player-name-${player.id}`}
                className={`flex-1 text-xs sm:text-sm truncate ${
                  isDisabled ? "text-slate-600" : "text-slate-100"
                }`}
              >
                {formatDisplayName(player.full_name)}
              </span>
              {(() => {
                const goalCount = goalCountByPlayerId?.[player.id] ?? 0;
                if (goalCount <= 0) return null;
                return (
                  <span
                    data-testid={`player-goal-indicator-${player.id}`}
                    role="img"
                    aria-label={t(
                      "playerGoalsScored",
                      "{{count}} goal(s) scored",
                      {
                        count: goalCount,
                      },
                    )}
                    title={t("playerGoalsScored", "{{count}} goal(s) scored", {
                      count: goalCount,
                    })}
                    className="inline-flex items-center gap-0.5 text-xs font-bold text-slate-100 shrink-0"
                  >
                    <span aria-hidden="true">⚽</span>
                    {goalCount > 1 && <span>{goalCount}</span>}
                  </span>
                );
              })()}
              <span
                data-testid={`player-position-${player.id}`}
                className={`text-[9px] uppercase tracking-wider border px-1.5 py-0.5 rounded-full ${meta.badge}`}
              >
                {player.position || meta.label}
              </span>
              {isExpelled && (
                <span
                  data-testid={`player-disabled-badge-${player.id}`}
                  className="inline-flex items-center rounded-md border border-slate-500/70 bg-slate-700/70 px-1.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-200"
                >
                  {t("tacticalField.disabledBadge", "Disabled")}
                </span>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );

  const selectionLocked = isReadOnly;
  // Full blocking overlay only fires when BOTH selection-locking AND drag are
  // disabled.  When repositionEnabled=true (the default), players can always
  // be dragged/repositioned — only click-to-log is blocked, and a subtle
  // "Drag-only mode" banner is shown instead of the full opaque overlay.
  const fullOverlayBlocked = selectionLocked && !repositionEnabled;
  // PMA-COCK-152 addendum: `repositionEnabled=false` was, until now, only
  // ever consulted above for `fullOverlayBlocked` (a `pointer-events-none`
  // banner — see its rendering below — that never actually stops a pointer
  // gesture from reaching the field). The raw `onPlayerDragEnd` prop was
  // passed straight through to TacticalField unconditionally, so a locked
  // cockpit (e.g. a COMPLETED match, via ActionStage's
  // `repositionEnabled={!cockpitLocked}`) still silently committed position
  // writes — this prop's own doc comment above ("Set to false only if drag
  // must also be disabled") was never actually honoured. Wrap the commit
  // itself here instead of gating the raw `onPlayerDragEnd` prop directly
  // (which the `resolvedViewMode === "tactical" ... && onPlayerDragEnd`
  // check below treats as "is drag support configured at all" — passing
  // `undefined` when merely locked would unmount the whole tactical field
  // instead of leaving it visible-but-read-only). A refused drag still
  // shows the live preview during the gesture (unaffected, driven by
  // `onDragStart`/`onDragMove`) and then snaps back on release, since no
  // position is committed and the canonical positions Map is unchanged —
  // the same "gesture starts, nothing persists" shape as a cancelled drag
  // (PMA-COCK-150's `onDragCancel`).
  const guardedOnPlayerDragEnd = useCallback<
    NonNullable<PlayerSelectorPanelProps["onPlayerDragEnd"]>
  >(
    (playerId, displayX, displayY, playerPosition, side) => {
      if (!repositionEnabled) return;
      onPlayerDragEnd?.(playerId, displayX, displayY, playerPosition, side);
    },
    [repositionEnabled, onPlayerDragEnd],
  );
  const resolvedFieldOverlay = fieldOverlay;
  const isPitchViewMode =
    resolvedViewMode === "tactical" || resolvedViewMode === "field";
  const showFloatingHeaderAction = Boolean(headerAction) && isPitchViewMode;
  // R3 (iPad viewport fix): in pitch view modes the "Drag-only mode" hint
  // must NOT render in-flow above the field. It used to sit in the normal
  // document flow right above the tactical/field wrapper, so the instant
  // `selectionLocked` flipped (e.g. tapping the "Resume Effective Time"
  // floating button, which flips clockMode INEFFECTIVE -> EFFECTIVE in the
  // same tick) the banner's ~48px unmounted, the pitch below it shifted up
  // by that amount, and the operator's tap point jumped. Rendering it
  // absolutely-positioned inside the same floating anchor as the resume
  // button (below) removes it from flow entirely, so toggling it can never
  // change the height of anything above the pitch — same fix shape as the
  // existing floating resume button, which is already proven jump-free.
  const dragOnlyHintPill = selectionLocked ? (
    <div
      className="pointer-events-auto w-full max-w-md rounded-lg border border-amber-500/40 bg-amber-900/90 backdrop-blur-sm px-3 py-2 flex items-center gap-2 text-amber-200 text-xs sm:text-sm shadow-lg"
      data-testid="drag-only-mode-banner"
    >
      <div className="h-2 w-2 rounded-full bg-amber-400 animate-pulse shrink-0" />
      <span className="font-medium shrink-0">
        {t("dragOnlyMode", "Drag-only mode")}
      </span>
      <span className="text-amber-200/70" data-testid="drag-only-hint">
        —{" "}
        {resolvedViewMode === "tactical" && substitutionEnabled
          ? t(
              "dragOnlySubHint",
              "You can reposition and substitute players. Start the clock to log actions.",
            )
          : t(
              "dragOnlyHint",
              "You can reposition players. Start the clock to log actions.",
            )}
      </span>
    </div>
  ) : null;

  return (
    <div
      // max-[1180px]: reclaims card padding on tablet/phone (the client's iPad
      // "fits one screen" ask, team_findings.md 2026-08-14) without touching
      // desktop, where p-6 stays. Same 1180px threshold TacticalField.tsx's
      // own pitch-height cap uses, kept in sync deliberately.
      // max-[1180px]:landscape: (PMA-COCK-106) goes tighter still, ONLY in
      // the rail layout specifically — that breakpoint no longer has
      // MatchTimerDisplay/TeamSelector/RefereeActionBar stacked above it
      // (relocated to the rail), so every remaining px here goes straight
      // toward getting the pitch's own bottom edge inside the viewport.
      // PMA-COCK-121 (2026-08-16): p-1.5 -> p-0.5 reclaims a further 8px of
      // horizontal dead width (4px/side) beside the pitch at iPad landscape
      // (client ask: "make bigger the soccer field"). Desktop/portrait
      // unaffected.
      className="bg-slate-800 rounded-lg shadow p-6 max-[1180px]:p-3 max-[820px]:portrait:p-1.5 max-[1180px]:landscape:p-0.5 border border-slate-700 relative overflow-visible"
      data-testid="player-grid"
    >
      {fullOverlayBlocked && (
        <div className="absolute inset-0 z-10 pointer-events-none bg-slate-900/70 backdrop-blur-sm border border-amber-500/40">
          <div className="p-4 flex items-start gap-3 text-amber-200">
            <div className="mt-0.5 h-2 w-2 rounded-full bg-amber-400 animate-pulse" />
            <div>
              <p className="font-semibold text-amber-100">
                {t("playerSelectLocked", "Player selection is paused")}
              </p>
              <p className="text-sm text-amber-200/80">
                {t(
                  "playerSelectLockedHint",
                  "Start the clock to enable selecting players.",
                )}
              </p>
            </div>
          </div>
          {resolvedFieldOverlay && (
            <div className="absolute inset-0">
              <div className="relative h-full w-full">
                {resolvedFieldOverlay}
              </div>
            </div>
          )}
        </div>
      )}
      {/* Pitch view modes (tactical/field) render this hint as a floating
          overlay instead — see dragOnlyHintPill / isPitchViewMode above —
          so its appearance/disappearance never reflows the field below. */}
      {selectionLocked && !isPitchViewMode && dragOnlyHintPill}
      <div className="flex items-center justify-between gap-3 mb-4 max-[1180px]:mb-2 max-[820px]:portrait:mb-1 max-[1180px]:landscape:mb-1">
        <div className="flex min-w-0 items-center gap-3">
          <h2 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
            <Users size={20} />
            {headerLabelOverride ?? t("selectPlayer", "Select Player")}
          </h2>
          {!showFloatingHeaderAction ? headerAction : null}
        </div>
        {!forceFieldMode && !forceTacticalMode && (
          <div className="flex bg-slate-900 rounded-lg p-1">
            <button
              onClick={() => setViewMode("list")}
              className={`p-1.5 rounded-md ${
                viewMode === "list"
                  ? "bg-slate-700 shadow text-blue-400"
                  : "text-slate-500 hover:text-slate-300"
              }`}
              title="List View"
            >
              <LayoutGrid size={18} />
            </button>
            <button
              onClick={() => setViewMode("field")}
              className={`p-1.5 rounded-md ${
                viewMode === "field"
                  ? "bg-slate-700 shadow text-blue-400"
                  : "text-slate-500 hover:text-slate-300"
              }`}
              title="Field View"
            >
              <Map size={18} />
            </button>
            {getDisplayPosition && (
              <button
                onClick={() => setViewMode("tactical")}
                data-testid="btn-tactical-view"
                className={`p-1.5 rounded-md ${
                  viewMode === "tactical"
                    ? "bg-slate-700 shadow text-blue-400"
                    : "text-slate-500 hover:text-slate-300"
                }`}
                title="Tactical View"
              >
                <Crosshair size={18} />
              </button>
            )}
          </div>
        )}
      </div>

      {resolvedViewMode === "tactical" &&
      getDisplayPosition &&
      onPlayerDragEnd ? (
        <div className={`mb-6 relative ${selectionLocked ? "opacity-50" : ""}`}>
          {showFloatingHeaderAction || dragOnlyHintPill ? (
            <div className="pointer-events-none absolute inset-x-0 top-3 z-20 flex flex-col items-center gap-2 px-3">
              {dragOnlyHintPill}
              {showFloatingHeaderAction ? (
                <div
                  className="pointer-events-auto"
                  data-testid="resume-effective-floating-anchor"
                >
                  {headerAction}
                </div>
              ) : null}
            </div>
          ) : null}
          <TacticalField
            homeTeamName={match.home_team.short_name || match.home_team.name}
            awayTeamName={match.away_team.short_name || match.away_team.name}
            homeColorStyle={homePalette.tacticalStyle}
            awayColorStyle={awayPalette.tacticalStyle}
            homePlayers={match.home_team.players.filter((p) =>
              onFieldIds.home.has(p.id),
            )}
            awayPlayers={match.away_team.players.filter((p) =>
              onFieldIds.away.has(p.id),
            )}
            expelledPlayerIds={expelledPlayerIds}
            disciplinaryStatusByPlayer={disciplinaryStatusByPlayer}
            flipSides={flipSides}
            getDisplayPosition={getDisplayPosition}
            onPlayerDragEnd={guardedOnPlayerDragEnd}
            onPlayerClick={(player, anchor, location, side) => {
              if (selectionLocked) return;
              if (onFieldPlayerClick) {
                onFieldPlayerClick(player, anchor, location, side);
                return;
              }
              onPlayerClick(player);
            }}
            onDestinationClick={
              selectionLocked ? undefined : onFieldDestinationClick
            }
            overlay={resolvedFieldOverlay}
            overlayBlocksPitch={overlayBlocksPitch}
            showDestinationControls={showDestinationControls}
            shotGuideEdge={shotGuideEdge}
            draggingPlayerId={draggingPlayerId}
            onDragStart={onTacticalDragStart}
            onDragStop={onTacticalDragStop}
            allPositions={allTacticalPositions}
            visiblePlayerIds={visiblePlayerIds}
            isMatchLive={isMatchLive}
            homeTeamColorInfo={homeTeamColorInfo}
            awayTeamColorInfo={awayTeamColorInfo}
            onTeamColorSaved={onTeamColorSaved}
            t={t}
          />
        </div>
      ) : resolvedViewMode === "field" ? (
        <div className={`mb-6 relative ${selectionLocked ? "opacity-50" : ""}`}>
          {showFloatingHeaderAction || dragOnlyHintPill ? (
            <div className="pointer-events-none absolute inset-x-0 top-3 z-20 flex flex-col items-center gap-2 px-3">
              {dragOnlyHintPill}
              {showFloatingHeaderAction ? (
                <div
                  className="pointer-events-auto"
                  data-testid="resume-effective-floating-anchor"
                >
                  {headerAction}
                </div>
              ) : null}
            </div>
          ) : null}
          <SoccerField
            homeTeamName={match.home_team.short_name || match.home_team.name}
            awayTeamName={match.away_team.short_name || match.away_team.name}
            homePlayers={match.home_team.players.filter((p) =>
              onFieldIds.home.has(p.id),
            )}
            awayPlayers={match.away_team.players.filter((p) =>
              onFieldIds.away.has(p.id),
            )}
            expelledPlayerIds={expelledPlayerIds}
            disciplinaryStatusByPlayer={disciplinaryStatusByPlayer}
            flipSides={flipSides}
            onPlayerClick={(player, anchor, location, side) => {
              if (selectionLocked) return;
              if (onFieldPlayerClick) {
                onFieldPlayerClick(player, anchor, location, side);
                return;
              }
              onPlayerClick(player);
            }}
            onDestinationClick={
              selectionLocked ? undefined : onFieldDestinationClick
            }
            overlay={resolvedFieldOverlay}
            showDestinationControls={showDestinationControls}
            t={t}
          />
        </div>
      ) : (
        <div
          className={`mb-6 ${
            selectedTeam === "both" ? "grid grid-cols-2 gap-4" : ""
          }`}
        >
          {teamsToShow.map(({ labelColor, team, tone }) => {
            const teamOnField =
              tone === "home" ? onFieldIds.home : onFieldIds.away;
            const onFieldPlayers = prioritizePlayers(
              team.players.filter((p) => teamOnField.has(p.id)),
            );

            return (
              <div className="space-y-3" key={team.id}>
                <h3 className="font-medium text-slate-300 flex items-center gap-2 mb-3">
                  <span className={`w-2 h-2 rounded-full ${labelColor}`}></span>
                  {team.short_name || team.name}
                </h3>
                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                    {t("onPitch", "On Pitch")}
                  </div>
                  {renderPlayerGrid(onFieldPlayers, tone, selectionLocked)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PlayerSelectorPanel;
